import React, {useEffect, useRef} from "react";

import {useTexture} from "@react-three/drei";
import {AugmentedEvent, SimulationEventType} from "@repo/shared";
import {AdditiveBlending, Color} from "three";

import {useGame} from "~/contexts/game-manager/GameManagerProvider";

import {ParticleEffect, ParticleEffectRef} from "../particle-effect";

const maxParticles = 32 * 4;

/**
 * Listens for death events from the primary simulation and triggers a particle explosion.
 */
const DeathExplosion: React.FC = () => {
  const {gameManager} = useGame();
  const particleEffectRef = useRef<ParticleEffectRef>(null);
  const particleTexture = useTexture("/images/particle.png");

  // This effect sets up the event listener.
  useEffect(() => {
    // Do nothing if the gameManager isn't ready.
    if (!gameManager) return;

    // This single handler will react to any relevant death event.
    const handleSimulationEvent = (event: AugmentedEvent) => {
      // Only create an explosion for the primary player's simulation.
      if (!event.isPrimary) return;

      // Check if the event type is one that signifies a player's death.
      if (
        event.type === SimulationEventType.PlayerOutOfBounds ||
        event.type === SimulationEventType.PlayerHitHazard
      ) {
        // Ensure the event contains the position data we need.
        if (event.player.currentPosition) {
          // Emit the particle burst at the position of the death event.
          particleEffectRef.current?.emit({
            x: event.player.currentPosition.x,
            y: event.player.currentPosition.y,
            count: 32,
            spawnRate: 128, // High spawn rate for an explosive burst
          });
        }
      }
    };

    // Subscribe to the event stream from the GameManager.
    gameManager.on("simulation-event", handleSimulationEvent);

    // Return the cleanup function to unsubscribe when the component unmounts.
    return () => {
      gameManager.off("simulation-event", handleSimulationEvent);
    };
  }, [gameManager]); // The dependency array ensures we only subscribe once.

  return (
    <ParticleEffect
      ref={particleEffectRef}
      particleLifetime={2.0}
      maxParticles={maxParticles}
      sizeCurve={[
        {stop: 0, size: 0.2},
        {stop: 0.05, size: 3},
        {stop: 0.3, size: 0.4},
        {stop: 1, size: 0.1},
      ]}
      colorCurve={[
        {stop: 0, color: new Color("#ff3232")},
        {stop: 1, color: new Color("#000000")},
      ]}
      zIndex={5}
      gravity={3}
      velocityMultiplier={4}
      upwardBias={1}
    >
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial
        map={particleTexture}
        blending={AdditiveBlending}
        depthTest={false}
        transparent={true}
      />
    </ParticleEffect>
  );
};

export default DeathExplosion;
